import { useAuth } from '@/hooks/useAuth'
import { useNotificationsContext } from '@/context/NotificationsContext'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

function formatDate(date: Date | undefined): string {
  if (!date) return '—'
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date instanceof Date ? date : new Date((date as { seconds: number }).seconds * 1000))
}

export function AdminNotificationsPage() {
  const { user } = useAuth()
  const { notifications, loading, markRead, resolve } = useNotificationsContext()

  if (loading) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-bold">Notifications</h1>
        <p className="text-muted-foreground">Loading…</p>
      </div>
    )
  }

  function getStatus(n: { resolved: boolean; readBy: string[] }): 'Resolved' | 'Read' | 'Unread' {
    if (n.resolved) return 'Resolved'
    if (user && n.readBy.includes(user.uid)) return 'Read'
    return 'Unread'
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Notifications</h1>
        <p className="text-muted-foreground mt-1">
          System notifications and action items for admins.
        </p>
      </div>

      {notifications.length === 0 ? (
        <p className="text-muted-foreground">No notifications yet.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Type</TableHead>
              <TableHead>Title</TableHead>
              <TableHead>Body</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {notifications.map((n) => {
              const status = getStatus(n)
              const isResolved = status === 'Resolved'
              const isUnread = status === 'Unread'
              return (
                <TableRow key={n.id} className={isResolved ? 'opacity-50' : undefined}>
                  <TableCell>
                    <Badge variant="secondary" className="whitespace-nowrap">
                      {n.type.replace(/_/g, ' ')}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-medium">{n.title}</TableCell>
                  <TableCell className="text-sm text-muted-foreground max-w-xs truncate">
                    {n.body}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        isResolved ? 'outline' : isUnread ? 'destructive' : 'secondary'
                      }
                    >
                      {status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                    {formatDate(n.createdAt)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      {isUnread && user && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => void markRead(n.id, user.uid)}
                        >
                          Mark Read
                        </Button>
                      )}
                      {!isResolved && user && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => void resolve(n.id, user.uid)}
                        >
                          Resolve
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      )}
    </div>
  )
}
